import * as React from 'react';

import { getLocale } from '@rematter/common/locale';
import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  cn,
  humanReadablePercent,
  MeasuredValue,
  Money,
} from '@rematter/ui-web';

import { BrokeredOrderLineItemUpdate } from './types';

import { useBrokeredOrderLineItemsTotals } from '~/modules/brokerage/hooks/useBrokeredOrderLineItemsTotals';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { ItemMarginRecord } from '~/modules/brokerage/types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '~/modules/brokerage/types/brokeredOrderLineItem';
import {
  ComputedPriceCell,
  DeleteButtonCell,
  FooterCell,
  HeaderCell,
  InventoryItemSelect,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  QuantityFieldCell,
  UnitFieldCell,
} from '~/shared/components/line-items';

const BROKERED_ORDER_CREATE_LINE_ITEM_GRID =
  'grid-cols-[minmax(14rem,_2fr)_100px_1fr_1fr_1fr_1fr_var(--size-input)]';

interface CreateOrderRowProps {
  lineItem: BrokeredOrderLineItemRecord;
  disabled: boolean;
  onUpdateLineItem: (id: string, updates: BrokeredOrderLineItemUpdate) => void;
  onDeleteLineItem: (id: string) => void;
}

const CreateOrderRow = React.memo<CreateOrderRowProps>(
  ({ lineItem, disabled, onUpdateLineItem, onDeleteLineItem }) => {
    const handleMaterialChange = React.useCallback(
      (itemId?: string) =>
        onUpdateLineItem(lineItem.id, { itemId: itemId ?? undefined }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleUnitChange = React.useCallback(
      (unit?: Unit) => {
        if (unit) onUpdateLineItem(lineItem.id, { unit });
      },
      [lineItem.id, onUpdateLineItem],
    );

    const handleQuantityChange = React.useCallback(
      (quantity: number) => onUpdateLineItem(lineItem.id, { quantity }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleDelete = React.useCallback(
      () => onDeleteLineItem(lineItem.id),
      [lineItem.id, onDeleteLineItem],
    );

    return (
      <LineItemRow gridTemplate={BROKERED_ORDER_CREATE_LINE_ITEM_GRID}>
        <InventoryItemSelect
          value={lineItem.itemId || undefined}
          onChange={handleMaterialChange}
          disabled={disabled}
        />
        <UnitFieldCell
          value={lineItem.unit}
          onChange={handleUnitChange}
          disabled={disabled}
        />
        <QuantityFieldCell
          value={lineItem.quantity}
          pricingUnit={lineItem.unit}
          disabled={disabled}
          onChange={handleQuantityChange}
        />
        <ComputedPriceCell value={lineItem.purchaseUnitPrice} />
        <ComputedPriceCell value={lineItem.salesUnitPrice} />
        <ComputedPriceCell
          value={lineItem.margin}
          subtitle={humanReadablePercent(lineItem.marginPercent ?? 0, {
            locale: getLocale(),
          })}
          showNegative
        />
        <DeleteButtonCell onDelete={handleDelete} disabled={disabled} />
      </LineItemRow>
    );
  },
);

interface BrokeredOrderCreateLineItemsTableProps {
  lineItems: BrokeredOrderLineItemRecord[];
  marginsData?: ItemMarginRecord[];
  disabled?: boolean;
  onUpdateLineItem: (id: string, updates: BrokeredOrderLineItemUpdate) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem: () => void;
}

export const BrokeredOrderCreateLineItemsTable: React.FC<
  BrokeredOrderCreateLineItemsTableProps
> = ({
  lineItems,
  marginsData,
  disabled = false,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
}) => {
  const t = useTranslations();
  const isEmpty = lineItems.length === 0;

  const totals = useBrokeredOrderLineItemsTotals(lineItems, marginsData);

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={disabled}
      disabled={disabled}
      addButtonLabel={t.orders.materials.buttons.addMaterial}
      emptyTitle={t.orders.materials.emptyState.title}
      emptyDescription={t.orders.materials.emptyState.description}
      className="@BrokeredOrderCreateLineItemsTable"
    >
      <LineItemHeader gridTemplate={BROKERED_ORDER_CREATE_LINE_ITEM_GRID}>
        <HeaderCell>{t.orders.materials.columns.material}</HeaderCell>
        <HeaderCell>{t.orders.materials.fields.unit}</HeaderCell>
        <HeaderCell>{t.orders.materials.fields.quantity}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.purchasePrice}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.salesPrice}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.margin}</HeaderCell>
        <HeaderCell />
      </LineItemHeader>

      <div className="flex flex-col gap-2">
        {lineItems.map((lineItem) => (
          <CreateOrderRow
            key={lineItem.id}
            lineItem={lineItem}
            disabled={disabled}
            onUpdateLineItem={onUpdateLineItem}
            onDeleteLineItem={onDeleteLineItem}
          />
        ))}
      </div>

      {!isEmpty && (
        <LineItemFooter
          gridTemplate={BROKERED_ORDER_CREATE_LINE_ITEM_GRID}
          className="text-sm"
        >
          <FooterCell className="font-semibold">
            {t.orders.materials.totals.totalMaterial(lineItems.length)}
          </FooterCell>
          <FooterCell />
          <FooterCell className="flex-col items-end gap-1 font-semibold">
            {!!totals.totalWeight && (
              <div>
                <MeasuredValue
                  value={totals.totalWeight.value}
                  unit={totals.totalWeight.unit}
                />
              </div>
            )}
            {!!totals.totalEachCount && (
              <div>
                <MeasuredValue
                  value={totals.totalEachCount}
                  unit={enumUnit.Each}
                />
              </div>
            )}
          </FooterCell>
          <FooterCell className="justify-end font-semibold">
            <Money {...totals.purchaseTotal} locale={getLocale()} />
          </FooterCell>
          <FooterCell className="justify-end font-semibold">
            <Money {...totals.salesTotal} locale={getLocale()} />
          </FooterCell>
          <FooterCell
            className={cn(
              'flex-col items-end font-semibold',
              totals.margin.amount < 0 && 'text-failure',
            )}
          >
            <div>
              <Money {...totals.margin} locale={getLocale()} />
            </div>
            <span className="text-xs font-normal text-neutral/60">
              {humanReadablePercent(totals.marginPercent, {
                locale: getLocale(),
              })}
            </span>
          </FooterCell>
          <FooterCell />
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
